import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { OrgProvider } from "./contexts/OrgContext";
import {
  SuperAdminProvider,
  useSuperAdmin,
} from "./contexts/SuperAdminContext";

// Public Pages
import LandingPage from "./pages/public/LandingPage";

// Admin Pages
import AdminDashboard from "./pages/Dashboard/Dashboard";
import LeadsList from "./pages/Leads/LeadsList";
import LeadDetails from "./pages/Leads/LeadDetails";
import AssignLeads from "./pages/Leads/AssignLeads";
import UsersList from "./pages/Users/UsersList";
import UserDetails from "./pages/Users/UserDetails";
import CallLogs from "./pages/CallLogs/CallLogs";
import RecordingsList from "./pages/Recordings/RecordingsList";
import Reports from "./pages/Reports/Reports";
import DailyWorkReports from "./pages/Reports/DailyWorkReports";
import Attendance from "./pages/Attendance/Attendance";
import Settings from "./pages/Settings/Settings";
import Leaderboard from "./pages/Leaderboard/Leaderboard";
import ProductsList from "./pages/Products/ProductsList";
import ScriptsList from "./pages/Scripts/ScriptsList";

// User/Telecaller Pages
import UserDashboard from "./pages/User/UserDashboard";
import UserLeads from "./pages/User/UserLeads";
import UserCallLogs from "./pages/User/UserCallLogs";

// Auth Pages
import Login from "./pages/Auth/Login";

// SuperAdmin Pages
import SuperAdminLogin from "./pages/superadmin/SuperAdminLogin";
import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import OrganizationsList from "./pages/superadmin/OrganizationsList";
import PlansList from "./pages/superadmin/PlansList";
import AdminsList from "./pages/superadmin/AdminsList";
import SuperAdminLeadsList from "./pages/superadmin/LeadsList";

// SuperAdmin Components
import SuperAdminLayoutComponent from "./components/SuperAdmin/SuperAdminLayout";

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      <p className="mt-4 text-gray-500">Loading...</p>
    </div>
  </div>
);

// Role-based Route Component
const RoleBasedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Return the children - routing is handled by AppRoutes
  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/app/" replace />;
  }

  return children;
};

// SuperAdmin Route Component
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSuperAdmin();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/superadmin/login" replace />;
  }

  return children;
};

// SuperAdmin Layout - using imported component
const SuperAdminLayout = SuperAdminLayoutComponent;

// Admin Routes
const AdminRoutes = () => (
  <Routes>
    <Route path="/" element={<AdminDashboard />} />
    <Route path="/leads" element={<LeadsList />} />
    <Route path="/leads/:id" element={<LeadDetails />} />
    <Route path="/leads/assign" element={<AssignLeads />} />
    <Route path="/users" element={<UsersList />} />
    <Route path="/users/:id" element={<UserDetails />} />
    <Route path="/call-logs" element={<CallLogs />} />
    <Route path="/recordings" element={<RecordingsList />} />
    <Route path="/reports" element={<Reports />} />
    <Route path="/reports/daily-work" element={<DailyWorkReports />} />
    <Route path="/leaderboard" element={<Leaderboard />} />
    <Route path="/attendance" element={<Attendance />} />
    <Route path="/settings" element={<Settings />} />
    <Route path="/settings/:tab" element={<Settings />} />
    <Route path="/products" element={<ProductsList />} />
    <Route path="/scripts" element={<ScriptsList />} />
    <Route path="*" element={<Navigate to="/app/" replace />} />
  </Routes>
);

// User/Telecaller Routes
const UserRoutes = () => (
  <Routes>
    <Route path="/" element={<UserDashboard />} />
    <Route path="/leads" element={<UserLeads />} />
    <Route path="/call-logs" element={<UserCallLogs />} />
    <Route path="*" element={<Navigate to="/app/" replace />} />
  </Routes>
);

// App Routes based on role
const AppRoutes = () => {
  const { isAdmin, isTelecaller, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show Admin Panel for admin users
  if (isAdmin) {
    return <AdminRoutes />;
  }

  // Show User Panel for telecaller users
  if (isTelecaller) {
    return <UserRoutes />;
  }

  // Default fallback - redirect to login
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/plans" element={<LandingPage />} />

            {/* Login Route */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            <Route
              path="/app/*"
              element={
                <RoleBasedRoute>
                  <OrgProvider>
                    <AppRoutes />
                  </OrgProvider>
                </RoleBasedRoute>
              }
            />
            {/* Backward compatibility - redirect /dashboard etc to /app/ */}
            <Route
              path="/dashboard"
              element={<Navigate to="/app/" replace />}
            />
            <Route
              path="/leads"
              element={<Navigate to="/app/leads" replace />}
            />
            <Route
              path="/users"
              element={<Navigate to="/app/users" replace />}
            />
            <Route
              path="/call-logs"
              element={<Navigate to="/app/call-logs" replace />}
            />
            <Route
              path="/reports"
              element={<Navigate to="/app/reports" replace />}
            />
            <Route
              path="/attendance"
              element={<Navigate to="/app/attendance" replace />}
            />
            <Route
              path="/settings"
              element={<Navigate to="/app/settings" replace />}
            />

            {/* SuperAdmin Routes */}
            <Route
              path="/superadmin/login"
              element={
                <SuperAdminProvider>
                  <SuperAdminLogin />
                </SuperAdminProvider>
              }
            />
            <Route
              path="/superadmin"
              element={
                <SuperAdminProvider>
                  <SuperAdminRoute>
                    <SuperAdminLayout />
                  </SuperAdminRoute>
                </SuperAdminProvider>
              }
            >
              <Route path="dashboard" element={<SuperAdminDashboard />} />
              <Route path="organizations" element={<OrganizationsList />} />
              <Route path="plans" element={<PlansList />} />
              <Route path="leads" element={<SuperAdminLeadsList />} />
              <Route path="admins" element={<AdminsList />} />
              <Route
                path=""
                element={<Navigate to="/superadmin/dashboard" replace />}
              />
            </Route>

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#333",
            color: "#fff",
            borderRadius: "8px",
          },
          success: {
            iconTheme: { primary: "#10B981", secondary: "#fff" },
          },
          error: {
            iconTheme: { primary: "#EF4444", secondary: "#fff" },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
